import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Col,
  Row,
  Space,
  Spin,
  Typography,
  Modal,
  Tooltip,
  Input,
  InputRef,
  Button,
} from "antd";
import { useParams } from "react-router-dom";
import { FinetuningService } from "../../services/FinetuningService";
import { usePermissions } from "../../PermissionsProvider";
import AnnotatorTool from "./AnnotatorTool";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  SearchOutlined,
  ExportOutlined,
  LoadingOutlined,
  SendOutlined,
  InfoCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  AnnotationMetadata,
  RequestData,
  GetSessionsResponse,
  AnnotationSession,
  ExportRequest,
} from "../../model_gen/finetuning";
import moment from "moment";
import FinetuneSettings from "../shared/FinetuneSettings";
import CustomSwitch from "../shared/switch/CustomSwitch";

const { Title } = Typography;
const { confirm } = Modal;
import "../../styles/components/Search.css";
const AnnotationView: React.FC = () => {
  const { apiKey } = usePermissions();
  const { companyId, appId, intentId } = useParams<{
    companyId: string;
    appId: string;
    intentId: string;
  }>();
  const [sessionData, setSessionData] = useState<AnnotationSession[]>([]);
  const [metadata, setMetadata] = useState<AnnotationMetadata | undefined>(
    undefined
  );
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [requestData, setRequestData] = useState<RequestData[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<RequestData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  const [labels, setLabels] = useState<{ [key: string]: RequestData["label"] }>(
    {}
  );
  const [completedSessions, setCompletedSessions] = useState<Set<string>>(
    new Set()
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const inputRef = useRef<InputRef>(null);
  const [isExportModalVisible, setIsExportModalVisible] =
    useState<boolean>(false);
  const [exportPrompt, setExportPrompt] = useState<string>("");
  const [removeExamples, setRemoveExamples] = useState<boolean>(true);
  const [removeExampleTag, setRemoveExampleTag] = useState<boolean>(false);
  const [removeOutputTag, setRemoveOutputTag] = useState<boolean>(false);
  const [runFinetuning, setRunFinetuning] = useState<boolean>(true);
  const [fourbit, setFourbit] = useState<boolean>(true);
  const [baseModel, setBaseModel] = useState<string>("");
  const [displayName, setDisplayName] = useState<string>("");
  const [promptDeletions, setPromptDeletions] = useState<string[]>([]);
  const [isAnnotationLoading, setIsAnnotationLoading] =
    useState<boolean>(false);
  const [metrics, setMetrics] = useState<{
    total_count: number;
    included_count: number;
    excluded_count: number;
  } | null>(null);
  const [warmupSteps, setWarmupSteps] = useState<number>(5);
  const [learningRate, setLearningRate] = useState<number>(2e-4);
  // const [targetedIntentId, setTargetedIntentId] = useState<string>("");

  useEffect(() => {
    const fetchSessionData = () => {
      if (companyId && appId && intentId) {
        FinetuningService.getSessions(
          appId,
          intentId,
          searchTerm,
          setIsLoading,
          apiKey
        ).subscribe(
          (data: GetSessionsResponse) => {
            setSessionData(data.sessions);
            setMetadata(data.metadata);
          },
          (error: any) => {
            console.error("Error fetching session data:", error);
          }
        );
      }
    };

    fetchSessionData();
  }, [companyId, appId, intentId, apiKey, searchTerm]);

  useEffect(() => {
    const fetchMetrics = () => {
      if (companyId && appId && intentId) {
        FinetuningService.getAnnotationMetrics(
          appId,
          intentId,
          null,
          apiKey
        ).subscribe(
          (data: any) => {
            setMetrics(data);
          },
          (error: any) => {
            console.error("Error fetching metrics:", error);
          }
        );
      }
    };

    fetchMetrics();
  }, [companyId, appId, intentId, apiKey]);

  const fetchRequestData = () => {
    if (appId && intentId && selectedSession) {
      setIsLoading(true);
      setRequestData([]);
      setSelectedRequest(null);
      setLabels({});
      FinetuningService.getRequests(
        appId,
        intentId,
        selectedSession,
        searchTerm,
        () => {},
        apiKey
      ).subscribe(
        (data: RequestData[]) => {
          setRequestData(data);
          const firstUnsavedRequest = data.find((request) => !request.saved);
          setSelectedRequest(firstUnsavedRequest || data[0] || null);

          const newLabels: { [key: string]: RequestData["label"] } = {};
          data.forEach((request) => {
            if (request.label) {
              newLabels[request.request_id] = request.label;
            }
          });
          setLabels(newLabels);

          const allRequestsSaved = data.every(
            (request) => request.saved || request.excluded
          );
          if (allRequestsSaved) {
            setCompletedSessions((prev) => new Set(prev).add(selectedSession));
            setIsLoading(false);
          } else {
            setIsLoading(false);
            setCompletedSessions((prev) => {
              const newSet = new Set(prev);
              newSet.delete(selectedSession);
              return newSet;
            });
          }
        },
        (error: any) => {
          console.error("Error fetching request data:", error);
          setIsLoading(false);
        }
      );
    }
  };

  useEffect(() => {
    fetchRequestData();
  }, [companyId, appId, intentId, selectedSession, apiKey, searchTerm]);

  const handleSessionSelect = (sessionId: string) => {
    const unsavedChanges = Object.keys(labels).length > 0;
    if (unsavedChanges) {
      confirm({
        title: "Unsaved Changes",
        content:
          "You have unsaved changes. Are you sure you want to switch sessions? Your changes will be lost.",
        onOk() {
          setSelectedSession(sessionId);
          setSelectedRequest(null);
          setLabels({});
        },
        onCancel() {},
      });
    } else {
      setSelectedSession(sessionId);
      setSelectedRequest(null);
    }
  };

  const handleRequestSelect = (request: RequestData) => {
    const unsavedChanges = Object.keys(labels).length > 0;
    if (unsavedChanges) {
      confirm({
        title: "Unsaved Changes",
        content:
          "You have unsaved changes. Are you sure you want to switch requests? Your changes will be lost.",
        onOk() {
          setSelectedRequest(request);
        },
        onCancel() {},
      });
    } else {
      setSelectedRequest(request);
    }
  };

  const handleSave = (editedContent: RequestData["label"]) => {
    if (selectedRequest && selectedSession) {
      setIsAnnotationLoading(true);

      // Check previous state to determine if counts should be updated
      const prevSaved = selectedRequest.saved;
      const prevExcluded = selectedRequest.excluded;

      FinetuningService.updateAnnotation(
        appId!,
        intentId!,
        selectedSession,
        selectedRequest.request_id,
        editedContent,
        () => {},
        apiKey
      ).subscribe(
        () => {
          // Update metrics if this is a new save
          if (metrics) {
            setMetrics(
              (prev) =>
                prev && {
                  ...prev,
                  total_count: prev.total_count + (prevSaved ? 0 : 1),
                  included_count:
                    prev.included_count + (!prevSaved || prevExcluded ? 1 : 0),
                  excluded_count:
                    prev.excluded_count + (prevSaved && prevExcluded ? -1 : 0),
                }
            );
          }

          const updatedRequestData = requestData.map((request) =>
            request.request_id === selectedRequest.request_id
              ? { ...request, saved: true, label: editedContent }
              : request
          );
          setRequestData(updatedRequestData);

          const allRequestsComplete = updatedRequestData.every(
            (request) => request.saved || request.excluded
          );

          if (metadata) {
            if (allRequestsComplete) {
              setMetadata({
                ...metadata,
                incomplete_sessions: metadata.incomplete_sessions.filter(
                  (sessionId) => sessionId !== selectedSession
                ),
              });
              setCompletedSessions((prev) =>
                new Set(prev).add(selectedSession)
              );
            } else if (
              !metadata.incomplete_sessions.includes(selectedSession)
            ) {
              setMetadata({
                ...metadata,
                incomplete_sessions: [
                  ...metadata.incomplete_sessions,
                  selectedSession,
                ],
              });
            }
          }

          const currentIndex = requestData.findIndex(
            (r) => r.request_id === selectedRequest.request_id
          );
          const nextUnsavedRequest = requestData
            .slice(currentIndex + 1)
            .find((request) => !request.saved && !request.excluded);

          if (nextUnsavedRequest) {
            setSelectedRequest(nextUnsavedRequest);
          } else if (currentIndex < requestData.length - 1) {
            setSelectedRequest(requestData[currentIndex + 1]);
          } else {
            const currentSessionIndex = sessionData.findIndex(
              (session) => session.session_id === selectedSession
            );
            if (currentSessionIndex < sessionData.length - 1) {
              const nextSession = sessionData[currentSessionIndex + 1];
              setSelectedSession(nextSession.session_id);
            } else {
              setSelectedSession(null);
              setSelectedRequest(null);
            }
          }
          setIsAnnotationLoading(false);
        },
        (error: any) => {
          console.error("Error updating annotation:", error);
          setIsAnnotationLoading(false);
        }
      );
    }
  };

  const handleCancel = () => {
    setSelectedRequest(null);
  };

  const handleDiscard = () => {
    if (selectedRequest && selectedSession) {
      setIsAnnotationLoading(true);

      // Check previous state to determine if counts should be updated
      const prevSaved = selectedRequest.saved;
      const prevExcluded = selectedRequest.excluded;

      FinetuningService.excludeAnnotation(
        appId!,
        intentId!,
        selectedSession,
        selectedRequest.request_id,
        () => {},
        apiKey
      ).subscribe(
        () => {
          // Update metrics if this is a new exclusion
          if (metrics) {
            setMetrics(
              (prev) =>
                prev && {
                  ...prev,
                  total_count: prev.total_count + (prevSaved ? 0 : 1),
                  included_count:
                    prev.included_count + (prevSaved && !prevExcluded ? -1 : 0),
                  excluded_count:
                    prev.excluded_count + (!prevSaved || !prevExcluded ? 1 : 0),
                }
            );
          }

          setRequestData((prevData) =>
            prevData.map((request) =>
              request.request_id === selectedRequest.request_id
                ? { ...request, excluded: true }
                : request
            )
          );

          const currentIndex = requestData.findIndex(
            (r) => r.request_id === selectedRequest.request_id
          );
          const nextValidRequest = requestData
            .slice(currentIndex + 1)
            .find((request) => !request.excluded);

          if (nextValidRequest) {
            setSelectedRequest(nextValidRequest);
          } else if (currentIndex < requestData.length - 1) {
            setSelectedRequest(requestData[currentIndex + 1]);
          } else {
            const currentSessionIndex = sessionData.findIndex(
              (session) => session.session_id === selectedSession
            );
            if (currentSessionIndex < sessionData.length - 1) {
              const nextSession = sessionData[currentSessionIndex + 1];
              setSelectedSession(nextSession.session_id);
            } else {
              setSelectedSession(null);
              setSelectedRequest(null);
            }
          }
          setIsAnnotationLoading(false);
        },
        (error: any) => {
          console.error("Error excluding annotation:", error);
          setIsAnnotationLoading(false);
        }
      );
    }
  };

  const handleSearch = () => {
    setSearchTerm(inputValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation(); // Prevent the event from bubbling up
  };

  const handleExport = (includeAll: boolean) => {
    setIsExporting(true);
    const exportRequest: Partial<ExportRequest> = {
      prompt: exportPrompt,
      remove_examples: removeExamples,
      include_all: includeAll,
      remove_example_tag: removeExampleTag,
      remove_output_tag: removeOutputTag,
      fourbit: fourbit,
      base_model: baseModel,
      display_name: displayName,
      prompt_deletions: promptDeletions,
      warmup_steps: warmupSteps,
      learning_rate: learningRate,
      application_id: parseInt(appId!),
      intent_id: parseInt(intentId!),
      company_id: parseInt(companyId!),
      // targeted_intent_id: targetedIntentId ? parseInt(targetedIntentId) : undefined,
    };

    const service = runFinetuning
      ? FinetuningService.finetuneAnnotations
      : FinetuningService.exportAnnotations;

    service(exportRequest, () => {}, apiKey).subscribe(
      (response: any) => {
        if (!runFinetuning && response instanceof Blob) {
          // Handle export case with file download
          const url = window.URL.createObjectURL(response);
          const a = document.createElement("a");
          a.href = url;
          a.download = `annotations_export_${companyId}_${appId}_${intentId}.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        } else {
          // Handle finetune case with success message
          Modal.success({
            title: "Finetuning job scheduled successfully",
            content: response.job_name && (
              <div>
                <p>Job Name: {response.job_name}</p>
                <p>
                  Job Logs:{" "}
                  <a
                    href={response.logs_url}
                    target="_blank"
                    rel="noopener noreferrer">
                    {response.logs_url}
                  </a>
                </p>
                <p>
                  Weights & Biases URL:{" "}
                  <a
                    href={response.wandb_url}
                    target="_blank"
                    rel="noopener noreferrer">
                    {response.wandb_url}
                  </a>
                </p>
              </div>
            ),
          });
        }
        setIsExporting(false);
        setIsExportModalVisible(false);
        setExportPrompt("");
        setRemoveExamples(false);
      },
      (error: any) => {
        console.error(
          `Error ${runFinetuning ? "finetuning" : "exporting"} annotations:`,
          error
        );
        Modal.error({
          content: `Failed to ${
            runFinetuning ? "finetune" : "export"
          } annotations`,
        });
        setIsExporting(false);
      }
    );
  };

  const handleSendEmailNotification = () => {
    confirm({
      title: "Send Email Notification",
      content: `Are you sure you want to send an email notification to ${metadata?.contact_email}?`,
      onOk() {
        setIsSendingEmail(true);
        FinetuningService.sendAnnotationEmail(
          companyId!,
          () => {},
          apiKey
        ).subscribe(
          () => {
            setIsSendingEmail(false);
            Modal.success({
              content: "Email notification sent successfully.",
            });
          },
          (error: any) => {
            console.error("Error sending email notification:", error);
            setIsSendingEmail(false);
            Modal.error({
              content: "Failed to send email notification.",
            });
          }
        );
      },
      onCancel() {},
    });
  };

  return (
    <div>
      <Card className="annotation-view-wrapper">
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4}>
                {metadata
                  ? `${metadata.company_name} / ${metadata.app_name} / ${metadata.intent_name} - Annotations`
                  : "Annotate"}
              </Title>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <div className="search-container">
                <Input.Search
                  ref={inputRef}
                  placeholder="Search..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onSearch={handleSearch}
                  onKeyDown={handleKeyDown}
                  enterButton={
                    <Button icon={<SearchOutlined />} disabled={isLoading}>
                      {isLoading && <Spin size="small" />}
                    </Button>
                  }
                />
              </div>
            </Col>
            <Col span={12}>
              <div
                style={{
                  float: "right",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                }}>
                {metrics && (
                  <Typography.Text>
                    total: {metrics.total_count}, included:{" "}
                    {metrics.included_count}, excluded: {metrics.excluded_count}
                  </Typography.Text>
                )}
                <Space>
                  <Button
                    type="primary"
                    onClick={() => setIsExportModalVisible(true)}
                    disabled={isExporting}>
                    Export{" "}
                    {isExporting && (
                      <LoadingOutlined style={{ marginLeft: 8 }} />
                    )}
                    {!isExporting && <ExportOutlined />}
                  </Button>
                  <Button
                    onClick={handleSendEmailNotification}
                    disabled={isSendingEmail}>
                    Send Email Notification
                    {isSendingEmail ? (
                      <LoadingOutlined style={{ marginLeft: 8 }} />
                    ) : (
                      <SendOutlined style={{ marginLeft: 8 }} />
                    )}
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
          <div
            style={{
              height: "calc(100vh - 300px)",
              backgroundColor: "white",
              borderRadius: "10px",
              position: "relative",
            }}>
            {isLoading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 1000,
                }}>
                <Spin size="large" />
              </div>
            )}
            <Row style={{ borderBottom: "1px solid #ccc", padding: "10px" }}>
              <Col span={5}>
                <Title level={5}>Sessions</Title>
              </Col>
              <Col span={5}>
                <Title level={5}>Requests</Title>
              </Col>
              <Col span={14}>
                <Title level={5}>Details</Title>
              </Col>
            </Row>
            <Row style={{ height: "85%" }}>
              <Col span={5} style={{ height: "100%", overflowY: "auto" }}>
                {sessionData.map((session) => (
                  <Tooltip
                    key={session.session_id}
                    title={moment(session.date_created).format(
                      "YYYY-MM-DD hh:mm:ss A"
                    )}
                    placement="top">
                    <div
                      onClick={() => handleSessionSelect(session.session_id)}
                      style={{
                        cursor: "pointer",
                        padding: "10px",
                        backgroundColor:
                          selectedSession === session.session_id
                            ? "#e0e0e0"
                            : "transparent",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}>
                      {completedSessions.has(session.session_id) ||
                      metadata?.excluded_sessions.includes(
                        session.session_id
                      ) ? (
                        <Tooltip title="This session is complete or excluded">
                          <CheckCircleFilled style={{ color: "green" }} />
                        </Tooltip>
                      ) : metadata?.incomplete_sessions.includes(
                          session.session_id
                        ) ? (
                        <Tooltip title="This session has pending saved annotations">
                          <SyncOutlined spin style={{ color: "#1890ff" }} />
                        </Tooltip>
                      ) : (
                        <div style={{ width: "14px" }} />
                      )}
                      <span>{session.session_id}</span>
                    </div>
                  </Tooltip>
                ))}
              </Col>
              <Col
                span={5}
                style={{
                  borderLeft: "1px solid #ccc",
                  borderRight: "1px solid #ccc",
                  height: "100%",
                  overflowY: "auto",
                }}>
                {selectedSession ? (
                  requestData.map((request) => (
                    <Tooltip
                      key={request.request_id}
                      title={moment(request.date_created).format(
                        "YYYY-MM-DD hh:mm:ss A"
                      )}
                      placement="top">
                      <div
                        onClick={() => handleRequestSelect(request)}
                        style={{
                          cursor: "pointer",
                          padding: "10px",
                          backgroundColor:
                            selectedRequest?.request_id === request.request_id
                              ? "#e0e0e0"
                              : "transparent",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}>
                        {request.saved || request.excluded ? (
                          <Tooltip
                            title={
                              request.excluded
                                ? "This request has been excluded"
                                : "This request has been saved"
                            }>
                            {request.excluded ? (
                              <CloseCircleFilled style={{ color: "red" }} />
                            ) : (
                              <CheckCircleFilled style={{ color: "green" }} />
                            )}
                          </Tooltip>
                        ) : (
                          <div style={{ width: "14px" }} />
                        )}
                        <span>{request.request_id}</span>
                      </div>
                    </Tooltip>
                  ))
                ) : (
                  <p style={{ padding: "10px" }}>
                    Select a session to view requests
                  </p>
                )}
              </Col>
              <Col span={14} style={{ height: "100%", overflowY: "auto" }}>
                {selectedRequest ? (
                  <AnnotatorTool
                    data={{
                      ...selectedRequest,
                      label: labels[selectedRequest.request_id],
                    }}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onDiscard={handleDiscard}
                    isLoading={isAnnotationLoading}
                  />
                ) : (
                  <p style={{ padding: "10px" }}>
                    Select a request to view details
                  </p>
                )}
              </Col>
            </Row>
          </div>
        </Space>
      </Card>
      <Modal
        title="Export Annotations"
        open={isExportModalVisible}
        onCancel={() => {
          if (!isExporting) {
            setIsExportModalVisible(false);
          }
        }}
        maskClosable={!isExporting}
        closable={!isExporting}
        footer={null}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>
            <Title level={5} style={{ marginBottom: 16 }}>
              Data Settings
            </Title>
            <div
              className="configure-menu-list-item-wrap"
              style={{ marginBottom: 12 }}>
              <div>
                <span>
                  Run Finetuning
                  <Tooltip title="Run finetuning instead of exporting">
                    <InfoCircleOutlined
                      style={{ marginLeft: 8, color: "rgba(0, 0, 0, 0.45)" }}
                    />
                  </Tooltip>
                </span>
              </div>
              <div className="switch-btn-wrap">
                <CustomSwitch
                  className="custom-switch"
                  checked={runFinetuning}
                  onChange={(checked) => setRunFinetuning(checked)}
                  disabled={isExporting}
                />
              </div>
            </div>
            <div
              className="configure-menu-list-item-wrap"
              style={{ marginBottom: 12 }}>
              <div>
                <span>Remove Examples</span>
              </div>
              <div className="switch-btn-wrap">
                <CustomSwitch
                  className="custom-switch"
                  checked={removeExamples}
                  onChange={(checked) => setRemoveExamples(checked)}
                  disabled={isExporting}
                />
              </div>
            </div>
            <div
              className="configure-menu-list-item-wrap"
              style={{ marginBottom: 12 }}>
              <div>
                <span>
                  Remove Example Tags
                  <Tooltip title="Removes the <example> tags from the final output">
                    <InfoCircleOutlined
                      style={{ marginLeft: 8, color: "rgba(0, 0, 0, 0.45)" }}
                    />
                  </Tooltip>
                </span>
              </div>
              <div className="switch-btn-wrap">
                <CustomSwitch
                  className="custom-switch"
                  checked={removeExampleTag}
                  onChange={(checked) => setRemoveExampleTag(checked)}
                  disabled={isExporting}
                />
              </div>
            </div>
            <div
              className="configure-menu-list-item-wrap"
              style={{ marginBottom: 12 }}>
              <div>
                <span>
                  Remove Output Tags
                  <Tooltip title="Removes the <output> tags from the final output">
                    <InfoCircleOutlined
                      style={{ marginLeft: 8, color: "rgba(0, 0, 0, 0.45)" }}
                    />
                  </Tooltip>
                </span>
              </div>
              <div className="switch-btn-wrap">
                <CustomSwitch
                  className="custom-switch"
                  checked={removeOutputTag}
                  onChange={(checked) => setRemoveOutputTag(checked)}
                  disabled={isExporting}
                />
              </div>
            </div>
          </div>
          <FinetuneSettings
            datasetId={-1}
            exportPrompt={exportPrompt}
            setExportPrompt={setExportPrompt}
            fourbit={fourbit}
            setFourbit={setFourbit}
            isExporting={isExporting}
            baseModel={baseModel}
            setBaseModel={setBaseModel}
            displayName={displayName}
            setDisplayName={setDisplayName}
            promptDeletions={promptDeletions}
            setPromptDeletions={setPromptDeletions}
            warmupSteps={warmupSteps}
            setWarmupSteps={setWarmupSteps}
            learningRate={learningRate}
            setLearningRate={setLearningRate}
            // targetedIntentId={targetedIntentId}
            // setTargetedIntentId={setTargetedIntentId}
          />
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => handleExport(false)}
              disabled={isExporting}>
              {runFinetuning ? "Finetune Annotated" : "Export Annotated"}
            </Button>
            <Button onClick={() => handleExport(true)} disabled={isExporting}>
              {runFinetuning ? "Finetune All" : "Export All"}
            </Button>
            {isExporting && (
              <LoadingOutlined style={{ marginLeft: 8, fontSize: 16 }} />
            )}
          </Space>
        </Space>
      </Modal>
    </div>
  );
};

export default AnnotationView;
